<template>
  <UApp :toaster="toaster" :locale="locale">
    <NuxtLoadingIndicator />

    <!--
      Full-screen loading overlay OUTSIDE NuxtLayout.
      Covers sidebar/navbar while session restoration is in progress.
      Prevents dashboard layout from flashing before login redirect.
      Uses fixed positioning with high z-index to cover everything.
    -->
    <div
      v-if="isLoading"
      role="status"
      aria-live="polite"
      class="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-white"
    >
      <img
        src="/images/openbox_logo_blue_horizontal.png"
        alt="Openbox"
        class="w-48 animate-[pulse-zoom_2s_ease-in-out_infinite]"
      />
    </div>

    <NuxtLayout>
      <NuxtPage />
    </NuxtLayout>
  </UApp>
</template>

<script setup lang="ts">
  import type { ToasterProps } from '@nuxt/ui'
  import { es } from '@nuxt/ui/locale'
  import { useAuthStore } from '~/stores/auth'

  const toaster: ToasterProps = { position: 'top-right' }

  // Título por defecto (red de seguridad: si una hoja no fijara título, se
  // muestra "Openbox Cloud" en lugar de la URL) + sufijo de marca centralizado
  // (FB-002). El titleTemplate es una función, no la cadena "%s", para tratar
  // el caso raíz/vacío sin duplicar el sufijo.
  useHead({
    title: 'Openbox Cloud',
    titleTemplate: title =>
      title && title !== 'Openbox Cloud'
        ? `${title} · Openbox Cloud`
        : 'Openbox Cloud',
  })

  // Use Spanish El Salvador locale for proper number formatting (dot as decimal separator)
  const locale = extendLocale(es, {
    code: 'es-SV',
  })

  const route = useRoute()

  // Get auth store to check session restoration status
  const authStore = useAuthStore()

  // Check if current route is the login page
  const isLoginRoute = computed(() => route.path === '/auth/login')

  // Show loading indicator while session restoration is in progress (client-side only)
  // Never show loading on login route
  // Keep overlay visible until the user is authenticated OR redirected to login,
  // preventing a brief flash of dashboard layout between sessionRestored and redirect
  const isLoading = computed(() => {
    if (isLoginRoute.value) {
      return false
    }
    return !authStore.sessionRestored || !authStore.isAuthenticated
  })
</script>

<style>
  @keyframes pulse-zoom {
    0%,
    100% {
      transform: scale(0.95);
    }
    50% {
      transform: scale(1.05);
    }
  }
</style>
